//Functionality imports
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import './App.css';
import "@aws-amplify/ui-react/styles.css";
import { Authenticator, ThemeProvider, useTheme, View, Text, Heading, Button, useAuthenticator } from '@aws-amplify/ui-react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import SidebarMenu from './components/sidebarmenu_comp/sidebarmenu';
import { LanguageProvider, useLanguage } from './i18n';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import LanguageSelector from './components/LanguageSelector/LanguageSelector';
/* import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ToggleButtonGroup, ToggleButton } from '@aws-amplify/ui-react'; */

//Components imports
import ChatPage from './pages/ChatPage/chat';
import ContactPage from './pages/ContactPage/contact';
import FAQPage from './pages/FAQPage/faqpage';
// UserProfile functionality now integrated in SidebarMenu
import TyCPage from './pages/TyCPage/tycpage';
import GuiaPage from './pages/GuiaPage/guiapage';
//customization imports
// Logos del login y título según el dominio (app. vs knobloch.data-bunker.ai, etc.)
import { branding, applyBrandingFeatures } from './config/branding';
import GeoPage from './pages/GeoPage/geopage';
import MediaMonitoring from './components/SocialAnalytics/MediaMonitoring';
import PlanPage from './pages/Plan/planpage';
import AdminPage from './pages/AdminPage/adminpage';
// PowerBI imports
import ReportsPage from './pages/ReportsPage';
import { getAvailableReports } from './services/powerbiService';
// Price Alerts imports
import AlertsPage from './pages/AlertsPage/AlertsPage';
// Opportunities Dashboard
import OpportunitiesPage from './pages/OpportunitiesPage';
// Floating Chat
import FloatingChat from './components/FloatingChat/FloatingChat';
// Red de seguridad: sin ErrorBoundary, una excepcion de render deja la app en blanco
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { installGlobalErrorReporter } from './utils/errorReporter';
// Custom Reports
import ReportPage from './pages/ReportPage';
import ReportPrintPage from './pages/ReportPrintPage';
// Visuales (dashboards React config-driven — demo)
import VisualsPage from './pages/VisualsPage';
// DataBunker Insights (reportes HTML del equipo interno — POC)
import InsightsPage from './pages/InsightsPage';
// Visuales · <cliente> (tableros de clientes de la maqueta): rutas y flags; las páginas cargan diferidas.
import { CLIENTES as CLIENTES_VISUALES } from './pages/VisualesClientes/tableros';
// Knobloch (tableros de mercado Knobloch x Data Bunker). Carga diferida: recharts y
// los tableros sólo se descargan cuando alguien con el módulo entra.
const KnoblochPage = lazy(() => import('./pages/KnoblochPage'));
// Visuales: tableros publicados desde el portal (runtime sincronizado; carga diferida: trae ECharts y sus piezas).
const TablerosPage = lazy(() => import('./pages/TablerosPage/TablerosPage'));
// Pricing L'Oréal (Pricing Tool de L'Oréal LDB). Carga diferida igual que Knobloch.
const LorealPricingPage = lazy(() => import('./pages/LorealPricingPage'));
const TablerosCliente = lazy(() => import('./pages/VisualesClientes/TablerosCliente'));

// Custome Log in page
const components = {
  Header() {
    const { tokens } = useTheme();

    const logos = branding.loginLogos;

    return (
      <View textAlign="center" padding={tokens.space.large}>
        {logos.length > 1 ? (
          // Co-branding (p. ej. knobloch.data-bunker.ai): logos lado a lado con separador
          <div className="login-cobrand">
            {logos.map((logo, i) => (
              <React.Fragment key={logo.alt}>
                {i > 0 && <span className="login-cobrand-sep" aria-hidden="true" />}
                <img src={logo.src} alt={logo.alt} />
              </React.Fragment>
            ))}
          </div>
        ) : (
          <img src={logos[0].src} alt="Logo" className="login-logo" />
        )}
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>
          &copy; All Rights Reserved
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          {/*   Welcome! Sign In to start */}
        </Heading>
      );
    },
    Footer() {
      const [showMessage, setShowMessage] = useState(false);
      const handleClick = () => {
        setShowMessage(!showMessage); // Muestra el texto al hacer clic
      };
      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={handleClick}
            size="small"
            variation="menu"
          >
            Forgot your Password?
          </Button>
          {showMessage && <p>Contacte a su administrador</p>}
        </View>
      );
    },
  },

  SignUp: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Create a new account
        </Heading>
      );
    },
    Footer() {
      const { toSignIn } = useAuthenticator();

      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={toSignIn}
            size="small"
            variation="link"
          >
            Back to Sign In
          </Button>
        </View>
      );
    },
  },
  ConfirmSignUp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  SetupTotp: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
  ForgotPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
        //padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
        //level={3}
        >
          Enter Information
        </Heading>
      );
    },
    Footer() {
      return <Text></Text>;
    },
  },
  ConfirmResetPassword: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Enter Information:
        </Heading>
      );
    },
    Footer() {
      return <Text>Footer Information</Text>;
    },
  },
};
//Default values
const formFields = {
  signIn: {
    username: {
      placeholder: 'Enter your User',
    },
  },
  signUp: {
    password: {
      label: 'Password:',
      placeholder: 'Enter your Password:',
      isRequired: false,
      order: 1,
    },
    confirm_password: {
      label: 'Confirm Password:',
      order: 2,
    },
  },
  forceNewPassword: {
    password: {
      placeholder: 'Enter your Password:',
    },
  },
  forgotPassword: {
    username: {
      placeholder: 'Enter your email:',
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: 'Enter your Confirmation Code:',
      label: 'New Label',
      isRequired: false,
    },
    confirm_password: {
      placeholder: 'Enter your Password Please:',
    },
  },
  setupTotp: {
    QR: {
      totpIssuer: 'test issuer',
      totpUsername: 'amplify_qr_test_user',
    },
    confirmation_code: {
      label: 'New Label',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'New Label',
      placeholder: 'Enter your Confirmation Code:',
      isRequired: false,
    },
  },
};

/* //Custom Live Detector
const Customizationi18n = ({ onLivenessComplete }) => {
  const [language, setLanguage] = useState<string>('en');
  const [showLiveness, setShowLiveness] = useState(false);

  useEffect(() => {
    setShowLiveness(true);
  }, []);

  return (
    <View padding="large">
      <ToggleButtonGroup value={language} isExclusive onChange={(value) => setLanguage(value)}>
        <ToggleButton value="en">En</ToggleButton>
        <ToggleButton value="es">Es</ToggleButton>
      </ToggleButtonGroup>

      {showLiveness && (
        <FaceLivenessDetector
          sessionId={'sessionId'} // Aquí debes proporcionar un ID de sesión real.
          region="us-east-1"
          onAnalysisComplete={async () => {
            // Cuando el análisis de liveness se completa, llamar a onLivenessComplete
            onLivenessComplete();
          }}
          displayText={dictionary[language]}
        />
      )}
    </View>
  );
};
//Default values LiveDetector
const dictionary = {
  en: null,
  es: {
    photosensitivyWarningHeadingText: 'Advertencia de fotosensibilidad',
    photosensitivyWarningBodyText: 'Esta verificación muestra luces de colores. Tenga cuidado si es fotosensible.',
    goodFitCaptionText: 'Buen ajuste',
    tooFarCaptionText: 'Demasiado lejos',
    hintCenterFaceText: 'Centra tu cara',
    startScreenBeginCheckText: 'Comenzar a verificar',
  },
}; */

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Route guard: no muestra nada mientras cargan permisos, redirige si deshabilitado
const ProtectedRoute = ({ featureKey, userFeatures, featuresLoaded, fallback = "/", children }) => {
  if (!featuresLoaded) return null;
  if (userFeatures[featureKey] === false) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

// Componente interno que maneja la aplicación autenticada con PowerBI

function AuthenticatedApp({ signOut, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { t } = useLanguage();
  const { updateBrandingFromUserConfig } = useOrganization();

  // Determinar si estamos en la página del chat principal
  const isChatPage = location.pathname.startsWith('/chat/');

  // Vista de impresión de reportes: vive AFUERA del layout (sin sidebar,
  // header, ni floating chat) para que el PDF salga limpio y membretado.
  const isReportPrintPage = /^\/report\/[^/]+\/print\/?$/.test(location.pathname);

  // Estados para PowerBI Reports
  const [availableReports, setAvailableReports] = useState([]);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [currentReportPage, setCurrentReportPage] = useState('');
  const [userFeatures, setUserFeatures] = useState(() => {
    // Leer del caché para carga instantánea
    try {
      const cached = localStorage.getItem('userFeatures');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignorar errores de localStorage */ }
    return {
      chatAgent: false,
      powerbiReports: false,
      mediaMonitoring: false,
      priceAlerts: false,
      opportunities: false,
      fileUpload: false,
      geoAnalysis: false,
      webSearch: false,
      dataExport: false,
    };
  });
  const [featuresLoaded, setFeaturesLoaded] = useState(() => {
    try { return !!localStorage.getItem('userFeatures'); } catch (e) { return false; }
  });
  // Features recién llegadas del servidor (no las de localStorage): sólo para el
  // aterrizaje por dominio de abajo.
  const [serverFeatures, setServerFeatures] = useState(null);
  const landingDone = useRef(false);
  // Features efectivas según el dominio (config/branding.js): p. ej. en
  // knobloch.data-bunker.ai sólo quedan activos algunos módulos y en los demás
  // dominios no existe Knobloch. Menú, rutas y chat flotante usan éstas.
  const features = useMemo(() => applyBrandingFeatures(userFeatures), [userFeatures]);
  const [userOrganization, setUserOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [lastRefreshDate, setLastRefreshDate] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  // Telemetria de errores de JS del navegador (window.onerror + promesas sin
  // catch). Se manda a /api/track con action='js_error' para poder diagnosticar
  // pantallas en blanco sin pedirle al cliente que abra la consola.
  useEffect(() => {
    return installGlobalErrorReporter(user?.username);
  }, [user?.username]);

  // Cargar configuración del usuario y actualizar branding
  useEffect(() => {
    const loadUserConfig = async () => {
      if (!user?.username) return;

      try {
        const response = await fetch(
          `${STRANDS_API_URL}/api/user/config?username=${encodeURIComponent(user.username)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success !== false) {
            // Actualizar branding con los datos del usuario
            updateBrandingFromUserConfig(data);
            // Cargar features/permisos de modulos del usuario
            if (data.features) {
              setUserFeatures(prev => {
                const updated = { ...prev, ...data.features };
                // floatingChat solo viene si está configurada: que no se quede la del caché.
                if (!('floatingChat' in data.features)) delete updated.floatingChat;
                try { localStorage.setItem('userFeatures', JSON.stringify(updated)); } catch (e) { /* ignore */ }
                return updated;
              });
              setFeaturesLoaded(true);
              setServerFeatures(data.features);
              console.log('[App] Features cargadas desde user config:', data.features);
            }
            console.log('[App] Branding actualizado desde user config');
          }
        }
      } catch (error) {
        console.error('[App] Error cargando user config:', error);
        setFeaturesLoaded(true); // Marcar como cargado aun con error para no bloquear UI
      }
    };

    loadUserConfig();
  }, [user?.username, updateBrandingFromUserConfig]);

  // Aterrizaje por dominio (config/branding.js): en knobloch.data-bunker.ai el usuario
  // entra directo a /knobloch si tiene el módulo. Una sola vez por carga y sólo si llegó
  // a la raíz, para que después pueda navegar a "/" sin que lo regrese.
  useEffect(() => {
    const landing = branding.landing;
    if (!landing || !serverFeatures || landingDone.current) return;
    landingDone.current = true;
    if (serverFeatures[landing.feature] === true && location.pathname === '/') {
      navigate(landing.path, { replace: true });
    }
  }, [serverFeatures, location.pathname, navigate]);

  // Cargar reportes disponibles cuando el usuario se autentica
  useEffect(() => {
    const loadReports = async () => {
      if (!user?.username) return;

      try {
        setReportsLoading(true);
        console.log('[App] Cargando reportes para usuario:', user.username);

        const data = await getAvailableReports(user.username);

        if (data.reports && data.reports.length > 0) {
          setAvailableReports(data.reports);
          // Establecer el primer reporte como activo por defecto
          setCurrentReportId(data.reports[0].id);

          // Establecer la primera página del menú si existe
          if (data.reports[0].menu && data.reports[0].menu.length > 0) {
            setCurrentReportPage(data.reports[0].menu[0].name);
          }

          console.log('[App] Reportes cargados:', data.reports.length);
        }

        // Guardar información del usuario
        if (data.organization) setUserOrganization(data.organization);
        if (data.role) setUserRole(data.role);
        // NO mergear features del servicio PowerBI aquí - las features autoritativas
        // (incluyendo permisos de módulos) vienen de /api/user/config en loadUserConfig

      } catch (error) {
        console.error('[App] Error cargando reportes:', error);
      } finally {
        setReportsLoading(false);
      }
    };

    loadReports();
  }, [user?.username]);

  // Handler para cambiar de reporte (solo cambio de estado + navegación;
  // el log de acceso vive en el sidebar, donde está el click real).
  const handleReportChange = useCallback((reportId) => {
    if (reportId !== currentReportId) {
      setCurrentReportId(reportId);
      const report = availableReports.find(r => r.id === reportId);
      if (report?.menu && report.menu.length > 0) {
        setCurrentReportPage(report.menu[0].name);
      }
      navigate('/reports');
    }
  }, [currentReportId, availableReports, navigate]);

  // Handler para cambiar de página dentro del reporte
  const handleReportPageChange = useCallback((pageName) => {
    console.log('[App] Cambiando a página:', pageName);
    setCurrentReportPage(pageName);
    // Navegar a la página de reportes si no estamos ahí
    navigate('/reports');
  }, [navigate]);

  // Handler para cuando el reporte carga datos (fecha de actualización, etc.)
  const handleReportDataLoaded = useCallback((data) => {
    if (data.lastRefreshDate) {
      setLastRefreshDate(data.lastRefreshDate);
    }
    // NO mergear features del servicio PowerBI aquí - las features autoritativas
    // (incluyendo permisos de módulos) vienen de /api/user/config en loadUserConfig
  }, []);

  // Vista de impresión: sin sidebar, sin header de app, sin chat flotante.
  // Mantiene los providers (Organization, Auth, i18n) porque están en
  // niveles superiores; solo evita el chrome.
  if (isReportPrintPage) {
    return (
      <Routes>
        <Route path="/report/:id_report/print" element={<ReportPrintPage />} />
      </Routes>
    );
  }

  return (
    <div className="app-container">
      <button
        className="hamburger-button"
        onClick={toggleSidebar}
      >
        ☰
      </button>
      <SidebarMenu
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
        // Props para PowerBI reports
        availableReports={availableReports}
        currentReportId={currentReportId}
        currentReportPage={currentReportPage}
        onReportChange={handleReportChange}
        onReportPageChange={handleReportPageChange}
        userFeatures={features}
      />
      <div className={`content ${isSidebarVisible ? 'content-expanded' : 'content-collapsed'}`}>
        <header className="app-header">
          <div className="header-left">
            <p className="app-title">{t('appTitle')}</p>
          </div>
          <div className="header-right">
            <LanguageSelector />
          </div>
        </header>

        <main className="app-main">
          <ErrorBoundary username={user?.username} module="app">
          <Routes>
            <Route path="/" element={
              <ProtectedRoute featureKey="mediaMonitoring" userFeatures={features} featuresLoaded={featuresLoaded} fallback="/reports">
                <MediaMonitoring userFeatures={features} />
              </ProtectedRoute>
            } />
            <Route path="/chat/:id_conversation" element={
              <ProtectedRoute featureKey="chatAgent" userFeatures={features} featuresLoaded={featuresLoaded}>
                <ChatPage userFeatures={features} />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TyCPage />} />
            <Route path="/guia" element={<GuiaPage />} />
            <Route path="/plan" element={<PlanPage />} />
            <Route path="/geo/:id_conversation" element={<GeoPage />} />
            <Route path="/admin" element={<AdminPage userFeatures={features} />} />
            <Route
              path="/reports"
              element={
                <ProtectedRoute featureKey="powerbiReports" userFeatures={features} featuresLoaded={featuresLoaded}>
                  <ReportsPage
                    availableReports={availableReports}
                    currentReportId={currentReportId}
                    currentPage={currentReportPage}
                    onReportDataLoaded={handleReportDataLoaded}
                    onPageChange={handleReportPageChange}
                  />
                </ProtectedRoute>
              }
            />
            <Route path="/alerts" element={
              <ProtectedRoute featureKey="priceAlerts" userFeatures={features} featuresLoaded={featuresLoaded}>
                <AlertsPage />
              </ProtectedRoute>
            } />
            <Route path="/opportunities" element={
              <ProtectedRoute featureKey="opportunities" userFeatures={features} featuresLoaded={featuresLoaded}>
                <OpportunitiesPage />
              </ProtectedRoute>
            } />
            <Route path="/report/:id_report" element={<ReportPage />} />
            {/* Visuales: el backend prende `visuals` si la organización tiene al menos un tablero
                publicado (se arman y publican en el portal, Reports › Tableros). Sin el flag se
                regresa a "/" (el backend también valida cada tablero y cada dataset). */}
            <Route path="/visuals" element={
              !featuresLoaded ? null
                : features.visuals === true
                  ? <Suspense fallback={null}><TablerosPage /></Suspense>
                  : <Navigate to="/" replace />
            } />
            {/* Visuales demo: el módulo config-driven anterior (Pricing Analytics demo). Solo con
                module_features.visualsDemo (la cuenta demo, desde Reports › Usuarios del portal). */}
            {features.visualsDemo === true && (
              <Route path="/visuals-demo" element={<VisualsPage />} />
            )}
            {/* DataBunker Insights: el backend prende `insights` si el usuario tiene al menos
                un reporte HTML publicado (se asignan desde el portal, Reports › HTML). Sin el
                flag se regresa a "/" (el backend también valida cada reporte). */}
            <Route path="/insights" element={
              !featuresLoaded ? null
                : features.insights === true
                  ? <InsightsPage />
                  : <Navigate to="/" replace />
            } />
            {/* Knobloch: flag `knobloch` en module_features (BD), default apagado.
                Opt-in: sin el flag en true se regresa a "/" (el backend también valida). */}
            <Route path="/knobloch" element={
              !featuresLoaded ? null
                : features.knobloch === true
                  ? <Suspense fallback={null}><KnoblochPage /></Suspense>
                  : <Navigate to="/" replace />
            } />
            {/* Pricing L'Oréal: flag `lorealPricing` en module_features (BD), default apagado.
                Opt-in: sin el flag en true se regresa a "/" (el backend también valida). */}
            <Route path="/loreal-pricing" element={
              !featuresLoaded ? null
                : features.lorealPricing === true
                  ? <Suspense fallback={null}><LorealPricingPage /></Suspense>
                  : <Navigate to="/" replace />
            } />
            {/* Visuales · Nike y Visuales · Tiendas Bara: tableros de la maqueta (pages/VisualesClientes/tableros.js),
                flag por cliente en module_features (BD), default apagado. Sin el flag se regresa a "/" (el backend
                también valida, con el usuario del token). */}
            {Object.values(CLIENTES_VISUALES).map((cliente) => (
              <Route key={cliente.ruta} path={cliente.ruta} element={
                !featuresLoaded ? null
                  : features[cliente.feature] === true
                    ? <Suspense fallback={null}><TablerosCliente cliente={cliente} /></Suspense>
                    : <Navigate to="/" replace />
              } />
            ))}
          </Routes>
          </ErrorBoundary>
        </main>
      </div>
      {/* Floating Chat - shown on all pages except the main chat page.
          module_features.floatingChat (true/false, Reports › Usuarios del portal) decide la
          burbuja por si sola: asi hay chat ligero sin el agente robusto y robusto sin burbuja.
          Sin esa llave sigue a chatAgent (la burbuja usa el mismo /query-stream).
          La lista fija de "agente ligero" (Farmatodo, Benavides, naos-br-106) se migro a la
          base con floatingChat=true (migracion 041) y se quito del codigo.
          Se espera a featuresLoaded para no mostrarla antes de conocer los permisos. */}
      {!isChatPage && featuresLoaded &&
        (typeof features.floatingChat === 'boolean'
          ? features.floatingChat
          : features.chatAgent !== false) && (
        <ErrorBoundary username={user?.username} module="floating_chat" compact>
          <FloatingChat userFeatures={features} />
        </ErrorBoundary>
      )}
    </div>
  );
}

//App deployment
function App() {
  const { tokens } = useTheme();

  // Título de la pestaña según el dominio (después del login lo puede ajustar el
  // branding de la organización, ver OrganizationContext).
  useEffect(() => {
    if (branding.pageTitle) document.title = branding.pageTitle;
  }, []);
  const theme = {
    name: 'Auth Theme',
    tokens: {
      components: {
        authenticator: {
          router: {
            boxShadow: `0 0 16px ${tokens.colors.overlay['10']}`,
            borderWidth: '0',
          },
          form: {
            padding: `${tokens.space.medium} ${tokens.space.xl} ${tokens.space.medium}`,
          },
        },
        button: {
          primary: {
            backgroundColor: tokens.colors.neutral['100'],
          },
          link: {
            color: tokens.colors.purple['80'],
          },
        },
        fieldcontrol: {
          _focus: {
            boxShadow: `0 0 0 2px ${tokens.colors.purple['60']}`,
          },
        },
        tabs: {
          item: {
            color: tokens.colors.neutral['80'],
            _active: {
              borderColor: tokens.colors.neutral['100'],
              color: tokens.colors.purple['100'],
            },
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <View>
          <Authenticator hideSignUp formFields={formFields} components={components}>
            {({ signOut, user }) => (
              <OrganizationProvider>
                <Router>
                  {/* Boundary raiz: ni un error en el sidebar o el header debe
                      dejar al cliente con la pantalla en blanco. */}
                  <ErrorBoundary username={user?.username} module="app_root">
                    <AuthenticatedApp signOut={signOut} user={user} />
                  </ErrorBoundary>
                </Router>
              </OrganizationProvider>
            )}
          </Authenticator>
        </View>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;